import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAuthStore } from "../stores/authStore";
import {
  listUsers,
  getUserDetails,
  type UserListItem,
  type UserDetails,
  type BrokerageAccountInfo,
  type InvestmentInfo,
} from "../services/adminService";

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

function formatCurrency(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
}

// Investment card component
function InvestmentCard({ investment }: { investment: InvestmentInfo }) {
  const returnPct = parseFloat(investment.total_return_pct);
  const isPositive = returnPct >= 0;

  return (
    <div className="bg-surface-800/50 rounded-lg p-3 border border-surface-700/50">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-surface-100 text-sm">{investment.strategy_name}</span>
        {investment.is_active ? (
          <span className="px-2 py-0.5 text-xs rounded-full bg-accent-500/20 text-accent-400">Active</span>
        ) : (
          <span className="px-2 py-0.5 text-xs rounded-full bg-surface-600 text-surface-400">Closed</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-surface-500">Invested:</span>
          <span className="ml-1 text-surface-300">{formatCurrency(investment.invested_amount)}</span>
        </div>
        <div>
          <span className="text-surface-500">Current:</span>
          <span className="ml-1 text-surface-300">{formatCurrency(investment.current_value)}</span>
        </div>
        <div>
          <span className="text-surface-500">Return:</span>
          <span className={`ml-1 ${isPositive ? "text-accent-400" : "text-red-400"}`}>
            {isPositive ? "+" : ""}{returnPct.toFixed(2)}%
          </span>
        </div>
        <div>
          <span className="text-surface-500">Started:</span>
          <span className="ml-1 text-surface-300">{formatDate(investment.started_at)}</span>
        </div>
      </div>
    </div>
  );
}

// Brokerage account expandable card
function BrokerageAccountCard({ account }: { account: BrokerageAccountInfo }) {
  const [isExpanded, setIsExpanded] = useState(account.is_default);

  return (
    <div className="glass-card overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-surface-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            account.account_type === "live" ? "bg-accent-500/20" : "bg-blue-500/20"
          }`}>
            <svg className={`w-5 h-5 ${
              account.account_type === "live" ? "text-accent-400" : "text-blue-400"
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-medium text-surface-100">{account.account_name}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                account.account_type === "live" 
                  ? "bg-accent-500/20 text-accent-400" 
                  : "bg-blue-500/20 text-blue-400"
              }`}>
                {account.account_type.toUpperCase()}
              </span>
              {account.is_default && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                  Default
                </span>
              )}
            </div>
            <div className="text-sm text-surface-400">
              ID: {account.alpaca_account_id || "N/A"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-lg font-semibold text-surface-100">
              {formatCurrency(account.portfolio_value)}
            </div>
            <div className="text-xs text-surface-400">
              Cash: {formatCurrency(account.cash_balance)}
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-surface-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-surface-700/50">
          <div className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-surface-300">
                Investments ({account.investments?.length || 0})
              </h4>
              <span className="text-xs text-surface-500">
                {account.last_synced_at && `Last synced: ${formatRelativeTime(account.last_synced_at)}`}
              </span>
            </div>
            {account.investments && account.investments.length > 0 ? (
              <div className="space-y-2">
                {account.investments.map((inv) => (
                  <InvestmentCard key={inv.id} investment={inv} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-surface-500 italic">No investments in this account</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// User list view
function UserList({ onSelectUser }: { onSelectUser: (userId: string) => void }) {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await listUsers();
        setUsers(response.users || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load users");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (u) =>
      u.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-6">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-accent-500"
        />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-surface-800/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-700/50">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-surface-700/30 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                      {user.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-surface-100">{user.display_name}</span>
                        {user.is_admin && (
                          <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-surface-400">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.status === "active"
                      ? "bg-accent-500/20 text-accent-400"
                      : user.status === "pending"
                      ? "bg-yellow-500/20 text-yellow-400"
                      : "bg-red-500/20 text-red-400"
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-surface-400">
                  {formatDate(user.created_at)}
                </td>
                <td className="px-4 py-3 text-sm text-surface-400">
                  {user.last_login_at ? formatRelativeTime(user.last_login_at) : "Never"}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onSelectUser(user.id)}
                    className="px-3 py-1.5 text-sm bg-accent-500/20 text-accent-400 rounded-lg hover:bg-accent-500/30 transition-colors"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="px-4 py-8 text-center text-surface-500">
            No users found
          </div>
        )}
      </div>
    </div>
  );
}

// User detail view
function UserDetailView({ userId, onBack }: { userId: string; onBack: () => void }) {
  const [user, setUser] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setIsLoading(true);
        const details = await getUserDetails(userId);
        setUser(details);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load user details");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserDetails();
  }, [userId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="glass-card p-6">
        <button onClick={onBack} className="text-accent-400 hover:text-accent-300 mb-4 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Users
        </button>
        <p className="text-red-400">{error || "User not found"}</p>
      </div>
    );
  }

  // Safely default nullable fields from API
  const brokerageAccounts = user.brokerage_accounts || [];
  const activityStats = user.activity_stats || { total_sign_ins: 0, backtests_last_15_days: 0, total_investments: 0, active_investments: 0 };

  // Calculate totals
  const totalPortfolioValue = brokerageAccounts.reduce(
    (sum, acc) => sum + parseFloat(acc.portfolio_value || "0"),
    0
  );

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button onClick={onBack} className="text-accent-400 hover:text-accent-300 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Users
      </button>

      {/* User header card */}
      <div className="glass-card p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent-500 to-purple-600 flex items-center justify-center text-white text-2xl font-semibold">
              {user.display_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-surface-100">{user.display_name}</h2>
                {user.is_admin && (
                  <span className="px-2 py-0.5 text-xs rounded bg-purple-500/20 text-purple-400">
                    Admin
                  </span>
                )}
              </div>
              <p className="text-surface-400">{user.email}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-surface-500">
                <span>Status: <span className={`${
                  user.status === "active" ? "text-accent-400" : "text-yellow-400"
                }`}>{user.status}</span></span>
                <span>•</span>
                <span>MFA: {user.mfa_enabled ? `Enabled (${user.mfa_method})` : "Disabled"}</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-surface-100">
              {formatCurrency(totalPortfolioValue)}
            </div>
            <p className="text-sm text-surface-400">Total Portfolio Value</p>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="text-2xl font-bold text-surface-100">{formatDate(user.created_at)}</div>
          <p className="text-sm text-surface-400">Account Created</p>
        </div>
        <div className="glass-card p-4">
          <div className="text-2xl font-bold text-surface-100">
            {user.last_login_at ? formatRelativeTime(user.last_login_at) : "Never"}
          </div>
          <p className="text-sm text-surface-400">Last Sign-in</p>
        </div>
        <div className="glass-card p-4">
          <div className="text-2xl font-bold text-surface-100">{activityStats.total_sign_ins}</div>
          <p className="text-sm text-surface-400">Total Sign-ins</p>
        </div>
        <div className="glass-card p-4">
          <div className="text-2xl font-bold text-surface-100">{activityStats.backtests_last_15_days}</div>
          <p className="text-sm text-surface-400">Backtests (15 days)</p>
        </div>
      </div>

      {/* Investment summary */}
      <div className="glass-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xl font-bold text-surface-100">{brokerageAccounts.length}</div>
            <p className="text-sm text-surface-400">Brokerage Accounts</p>
          </div>
          <div>
            <div className="text-xl font-bold text-accent-400">{activityStats.active_investments}</div>
            <p className="text-sm text-surface-400">Active Investments</p>
          </div>
          <div>
            <div className="text-xl font-bold text-surface-100">{activityStats.total_investments}</div>
            <p className="text-sm text-surface-400">Total Investments</p>
          </div>
        </div>
      </div>

      {/* Brokerage accounts */}
      <div>
        <h3 className="text-lg font-semibold text-surface-100 mb-4">Brokerage Accounts</h3>
        {brokerageAccounts.length > 0 ? (
          <div className="space-y-4">
            {brokerageAccounts.map((account) => (
              <BrokerageAccountCard key={account.id} account={account} />
            ))}
          </div>
        ) : (
          <div className="glass-card p-6 text-center text-surface-500">
            No brokerage accounts connected
          </div>
        )}
      </div>

      {/* Roles */}
      {user.roles && user.roles.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-sm font-medium text-surface-300 mb-2">Roles</h3>
          <div className="flex flex-wrap gap-2">
            {user.roles.map((role) => (
              <span
                key={role.id}
                className="px-3 py-1 text-sm rounded-full bg-surface-700 text-surface-300"
              >
                {role.display_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Main page component
export function AdminUsersPage() {
  const { user } = useAuthStore();
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();

  const handleSelectUser = useCallback(
    (id: string) => {
      navigate(`/admin/users/${id}`);
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    navigate("/admin/users");
  }, [navigate]);

  // Check if user is admin (must be after all hooks)
  const isAdmin = user?.roles?.some((role) => role.name === "TradeCraft_Admin");

  if (!isAdmin) {
    return <Navigate to="/settings" replace />;
  }

  return (
    <div className="bg-background">
      <div className="container mx-auto px-4 py-8">
        <PageHeader
          title={userId ? "User Details" : "User Management"}
          subtitle={userId ? "View detailed user information" : "Manage all users on the platform"}
        />

        <div className="mt-8">
          {userId ? (
            <UserDetailView userId={userId} onBack={handleBack} />
          ) : (
            <UserList onSelectUser={handleSelectUser} />
          )}
        </div>
      </div>
    </div>
  );
}
